import { useTranslation } from 'react-i18next';
import {
  BasisToggleButton,
  BrandIcon,
  StatusTag,
} from '@/components/Approval/@components/OpenConstructionCost/parts';
import {
  canToggleSettlementBasis,
  hasSettlementBasis,
  settlementStatusTagClass,
  type OpenConstructionCostSettlementView,
  type SettlementBasisMode,
} from '@/utils/approval/openConstructionCostSettlement';

type Props = {
  view: OpenConstructionCostSettlementView;
  /** null 이면 기준별 데이터 없음 — kpi·roi 기본값(직접이익)으로 그림 */
  basisMode: SettlementBasisMode | null;
  onToggleBasis?: () => void;
};

const Tag = ({
  id,
  status,
  label,
  hidden,
}: {
  id: string;
  status: string;
  label?: string;
  hidden?: boolean;
}) => (
  <StatusTag
    id={id}
    status={status}
    variant={settlementStatusTagClass(status)}
    label={label}
    hidden={hidden}
  />
);

const Html = ({ id, html }: { id: string; html: string }) => (
  <td id={id} dangerouslySetInnerHTML={{ __html: html }} />
);

/**
 * 레거시 #open_construction_cost_settlement_content 안쪽 마크업 그대로(renderConstructionCost·applyBasis 결과).
 * 클래스·id 를 유지해야 저장 content 를 레거시 조회 화면이 같은 CSS·토글 스크립트로 그림
 */
const OpenConstructionCostSettlementReport = ({
  view,
  basisMode,
  onToggleBasis,
}: Props) => {
  const { t } = useTranslation();
  const { project: p, kpi: k, costStatus: c } = view;
  const basis = basisMode ? view.basis[basisMode] : null;
  const active = hasSettlementBasis(basis) ? basis : null;
  const canToggle = canToggleSettlementBasis(view);

  const opinionHTML = active?.opinionHTML || view.overallOpinionHTML || '-';
  const kr = active ? active.kpiRecovery : k.recovery;
  const kp = active ? active.kpiProfit : k.profit;
  const r = active ? active.roi : view.roi;
  const profitTitle = active?.profitTitle ?? t('월 직접이익률');
  const profitChip = active?.profitChip ?? t('직접이익률 판정');

  return (
    <>
      <div className="detail-section">
        <div className="section-title">{t('프로젝트 개요')}</div>
        <div className="info-grid">
          <div className="info-item">
            <div className="info-label">{t('브랜드')}</div>
            <div className="info-value brand-info">
              {p.brand ? (
                <BrandIcon brand={p.brand} />
              ) : (
                <div className="brand-logo mlb" style={{ display: 'none' }} />
              )}
              <span className="value_brand">{p.brand || '-'}</span>
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('프로젝트명')}</div>
            <div className="info-value" id="value_project_name">
              {p.projectName || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('채널')}</div>
            <div className="info-value" id="value_chanel">
              {p.channel || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('면적')}</div>
            <div className="info-value" id="value_area">
              {p.area || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('공사기간')}</div>
            <div className="info-value" id="value_construction_period">
              {p.period || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('오픈일')}</div>
            <div className="info-value" id="value_date_open">
              {p.openDate || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('정산 총공사비')}</div>
            <div className="info-value" id="value_settlement_total_cost">
              {p.settlementCost || '-'}
            </div>
          </div>
          <div className="info-item">
            <div className="info-label">{t('공사유형')}</div>
            <div className="info-value content" id="value_construction_type">
              {p.constructionType || '-'}
            </div>
          </div>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="cc-section overall-opinion">
        <div className="opinion-head">
          <span className="ai-pill">{t('AI 공사비 적정성 종합의견')}</span>
          <BasisToggleButton
            basisMode={basisMode}
            hidden={!canToggle}
            onClick={onToggleBasis}
          />
        </div>
        <p
          className="opinion-text"
          id="value_overall_opinion"
          dangerouslySetInnerHTML={{ __html: opinionHTML }}
        />
      </div>

      <div className="cc-section feasibility-cards">
        <div className="feasibility-card is-blue">
          <div className="card-head">
            <div className="card-title">{t('공사비 적정성')}</div>
            <Tag id="value_kpi_cost_status" status={k.cost.status} />
          </div>
          <div className="card-divider" />
          <div className="card-body">
            <div className="card-metric val-blue" id="value_kpi_cost_value">
              {k.cost.value || '-'}
            </div>
            <div className="card-caption" id="value_kpi_cost_desc">
              {k.cost.desc || '-'}
            </div>
          </div>
        </div>
        <div className="feasibility-card is-blue">
          <div className="card-head">
            <div className="card-title">{t('투자회수기간')}</div>
            <Tag id="value_kpi_recovery_status" status={kr.status} />
          </div>
          <div className="card-divider" />
          <div className="card-body">
            <div className="card-metric val-blue" id="value_kpi_recovery_value">
              {kr.value || '-'}
            </div>
            <div className="card-caption" id="value_kpi_recovery_desc">
              {kr.desc || '-'}
            </div>
          </div>
        </div>
        <div className="feasibility-card is-blue">
          <div className="card-head">
            <div className="card-title" id="label_kpi_profit_title">
              {profitTitle}
            </div>
            <Tag id="value_kpi_profit_status" status={kp.status} />
          </div>
          <div className="card-divider" />
          <div className="card-body">
            <div className="card-metric val-blue" id="value_kpi_profit_value">
              {kp.value || '-'}
            </div>
            <div className="card-caption" id="value_kpi_profit_desc">
              {kp.desc || '-'}
            </div>
          </div>
        </div>
      </div>

      <div className="cc-section feasibility-card is-blue">
        <div className="card-head">
          <div className="card-title">{t('공사비 적정성')}</div>
          <Tag id="value_cost_status" status={c.status} />
        </div>
        <div className="card-divider" />
        <div className="card-body">
          <div className="card-subtitle">{t('비교군 평단가 대비')}</div>
          <table className="mini-table">
            <tbody>
              <tr>
                <th>{t('비교군 평단가')}</th>
                <Html id="value_cost_benchmark" html={c.benchmarkCell || '-'} />
              </tr>
              <tr>
                <th>{t('오픈품의 기준')}</th>
                <Html id="value_cost_open" html={c.openCell || '-'} />
              </tr>
              <tr>
                <th>{t('정산 확정 기준')}</th>
                <Html
                  id="value_cost_settlement"
                  html={c.settlementCell || '-'}
                />
              </tr>
            </tbody>
          </table>
          <div className="card-note">
            <span className="note-chip">
              <span id="value_design_grade">{c.designGrade || '-'}</span>
            </span>
            <p className="note-desc mb-0" id="value_design_note">
              {c.designNote}
            </p>
          </div>
        </div>
      </div>

      <div className="cc-section feasibility-card is-violet">
        <div className="card-head">
          <div className="card-title">{t('투자회수 적정성')}</div>
          <Tag id="value_roi_status" status={r.status} label={t('회수 속도')} />
          <Tag
            id="value_roi_rate_status"
            status={kp.status}
            label={t('이익률')}
            hidden={!kp.status}
          />
        </div>
        <div className="card-divider" />
        <div className="card-body">
          <div className="roi-cols">
            <div className="roi-col">
              <div className="card-subtitle">{t('투자회수기간')}</div>
              <table className="mini-table">
                <tbody>
                  <tr>
                    <th>{t('적정 회수기간')}</th>
                    <td id="value_recovery_standard">
                      {r.standardRecovery || '-'}
                    </td>
                  </tr>
                  <tr>
                    <th>{t('오픈품의 기준')}</th>
                    <td id="value_recovery_open">{r.openRecovery || '-'}</td>
                  </tr>
                  <tr>
                    <th>{t('정산 확정 기준')}</th>
                    <td id="value_recovery_settlement">
                      {r.settlementRecovery ? (
                        <strong>{r.settlementRecovery}</strong>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="card-note">
                <span className="note-chip">{t('투자회수 판정')}</span>
                <p className="note-desc mb-0" id="value_recovery_verdict">
                  {r.recoveryVerdict}
                </p>
              </div>
            </div>
            <div className="roi-col">
              <div className="card-subtitle" id="label_profit_subtitle">
                {profitTitle}
              </div>
              <table className="mini-table">
                <tbody>
                  <tr>
                    <th>{t('적정기준')}</th>
                    <Html
                      id="value_profit_standard"
                      html={r.standardProfitCell || '-'}
                    />
                  </tr>
                  <tr>
                    <th>{t('오픈품의 기준')}</th>
                    <Html
                      id="value_profit_open"
                      html={r.openProfitCell || '-'}
                    />
                  </tr>
                  <tr>
                    <th>{t('정산 확정 기준')}</th>
                    <Html
                      id="value_profit_settlement"
                      html={r.settlementProfitCell || '-'}
                    />
                  </tr>
                </tbody>
              </table>
              <div className="card-note">
                <span className="note-chip" id="label_profit_chip">
                  {profitChip}
                </span>
                <p className="note-desc mb-0" id="value_profit_verdict">
                  {r.profitMarginVerdict}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr className="section-divider" />

      <div className="settlement-compare mt-40">
        <div className="section-title">{t('공사비 정산비교')}</div>
        <div className="cc-section special-notes">
          <div>
            <span className="ai-pill">
              {t('AI & 담당자 공사내용 특이사항 분석')}
            </span>
          </div>
          <div
            className="notes-body"
            id="value_special_notes"
            dangerouslySetInnerHTML={{ __html: view.specialNotesHTML || '-' }}
          />
        </div>
        <div className="table-container mt-10">
          <table
            id="settlement_compare_table"
            className="budget-table table table-fixed"
          >
            <colgroup>
              <col style={{ width: 200 }} />
              <col style={{ width: 210 }} />
              <col style={{ width: 210 }} />
              <col style={{ width: 210 }} />
              <col style={{ width: 181 }} />
              <col style={{ width: 181 }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t('공종')}</th>
                <th className="text-right">{t('비교군 평단가')}</th>
                <th className="text-right">{t('오픈품의 평단가')}</th>
                <th className="text-right">{t('정산 확정 평단가')}</th>
                <th className="text-right">{t('정산−오픈 차이')}</th>
                <th className="text-right">{t('정산−비교군 차이')}</th>
              </tr>
            </thead>
            <tbody>
              {view.comparisonRows.length ? (
                view.comparisonRows.map((row, i) => (
                  <tr key={`${row.type}-${i}`}>
                    <td>{row.type}</td>
                    <td className="text-right">{row.benchmarkPrice || '-'}</td>
                    <td className="text-right">{row.openPrice || '-'}</td>
                    <td className="text-right">{row.settlementPrice || '-'}</td>
                    <td
                      className="text-right"
                      dangerouslySetInnerHTML={{
                        __html: row.vsOpenCell || '-',
                      }}
                    />
                    <td
                      className="text-right"
                      dangerouslySetInnerHTML={{
                        __html: row.vsBenchmarkCell || '-',
                      }}
                    />
                  </tr>
                ))
              ) : (
                <tr>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="vendor-settlement mt-40">
        <div className="section-title">{t('업체별 정산금액')}</div>
        <div className="table-container mt-10">
          <table
            id="vendor_settlement_table"
            className="budget-table table table-fixed"
          >
            <colgroup>
              <col style={{ width: 298 }} />
              <col style={{ width: 298 }} />
              <col style={{ width: 298 }} />
              <col style={{ width: 298 }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t('공종')}</th>
                <th className="text-left">{t('업체명')}</th>
                <th className="text-right">{t('금액')}</th>
                <th className="text-right">
                  {t('구성비', { context: 'ratio' })}
                </th>
              </tr>
            </thead>
            <tbody>
              {view.vendorRows.length ? (
                view.vendorRows.map((row, i) => (
                  <tr key={`${row.type}-${row.vendorName}-${i}`}>
                    <td>{row.type}</td>
                    <td className="text-left">{row.vendorName || '-'}</td>
                    <td className="text-right">{row.amount || '-'}</td>
                    <td className="text-right">{row.compositionPct || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td>{t('합계')}</td>
                <td />
                <td className="text-right" id="value_vendor_total_amount">
                  {view.vendorRows.length
                    ? view.vendorTotal.amount || '-'
                    : '-'}
                </td>
                <td className="text-right" id="value_vendor_total_pct">
                  {view.vendorRows.length
                    ? view.vendorTotal.compositionPct || '-'
                    : '-'}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );
};

export default OpenConstructionCostSettlementReport;
